import React, { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import EditLegend from '../../../components/Legend/EditLegend';
import { convertLegendValues } from '../../../utils/processUnits';
import { parseLegendValues } from '../../../utils/parseLegendValues';
import { trackEvent } from '../../../actions/analyticsActions';

import {
  decrementSteps,
  editLegendValues,
  incrementSteps,
  setMaxValue,
  setMinValue,
  setSteps,
  toggleEditLegend,
} from '../../../actions/legendActions';
import { useAppSelector } from '../../../utils';
import {
  getCurrentLayerId,
  getCurrentSeason,
  getFieldUuid,
  usernameSelector,
  userSelector
} from '../../../selectors';
import { getIsEditLegendVisible, getlegend } from '../../../selectors/legendSelectors';
import { Interval, Unit, EventDates, IntervalWithValue } from '../../../types';

export type EditLegendContainerProps = {
  index: number;
  units: Unit;
  year: number;
  crop: string;
  dates: EventDates;
  intervals: Interval[];
  unitsLocaleGroup?: string;
  subLayer?: string;
};

export const EditLegendContainer = ({
  index,
  units,
  unitsLocaleGroup,
  year,
  crop,
  subLayer,
  dates,
  intervals
}: EditLegendContainerProps) => {

  const dispatch = useDispatch();
  const fieldUuid = useAppSelector(getFieldUuid).toUpperCase();
  const isEditLegendVisible = useAppSelector(state => getIsEditLegendVisible(state, { index }));
  const layerId = useAppSelector(state => getCurrentLayerId(state, { index }));
  const legend = useAppSelector(getlegend);
  const season = useAppSelector(state => getCurrentSeason(state, { index }));
  const username = useAppSelector(usernameSelector);
  const user = useAppSelector(userSelector);
  const itWasEditingLegends = useRef<boolean>(false);

  const isFormInvalid = () => Number(legend.maxValue) <= Number(legend.minValue);

  const setMax = useCallback((e) => {
    dispatch(setMaxValue(e.target.value));
  }, [dispatch]);

  const setMin = useCallback((e) => {
    dispatch(setMinValue(e.target.value));
  }, [dispatch]);

  const onToggleEditLegend = useCallback(() => {
    dispatch(toggleEditLegend(index));
    itWasEditingLegends.current = false;
  }, [dispatch, index]);

  const onDecrementSteps = useCallback(() => {
    dispatch(decrementSteps());
  }, [dispatch]);

  const onIncrementSteps = useCallback(() => {
    dispatch(incrementSteps());
  }, [dispatch]);

  const handleSubmit = useCallback(() => {
    const legendValues = convertLegendValues(
      legend,
      units,
      layerId,
      unitsLocaleGroup
    );
    // use the same units received from the FVP legend API GET call
    legendValues.unit = units.intervals[0].units;

    dispatch(editLegendValues(
      legendValues,
      username,
      fieldUuid,
      year,
      crop,
      layerId,
      season,
      subLayer,
      dates,
      user.uuid,
    ));
    onToggleEditLegend();

    dispatch(trackEvent('save_legend', 'clicked', {
      numberOfSteps: legendValues.steps,
      min: legendValues.min,
      max: legendValues.max,
    }));
  }, [
    dispatch,
    onToggleEditLegend,
    legend,
    units,
    layerId,
    unitsLocaleGroup,
    username,
    fieldUuid,
    year,
    crop,
    season,
    subLayer,
    dates,
    user
  ]);

  useEffect(() => {
    // We have to use the ref "itWasEditingLegends" because "intervals" is not a stable array.
    if (
      isEditLegendVisible &&
      !itWasEditingLegends.current &&
      intervals.length
    ) {
      const intervalsWithValues = intervals as IntervalWithValue[];
      // Remove any extraneous spaces or commas that were inserted during i18n number formatting
      const maxValue = parseLegendValues(intervalsWithValues[0].value);
      const minValue = parseLegendValues(intervalsWithValues[intervalsWithValues.length - 1].value);

      dispatch(setMaxValue(maxValue));
      dispatch(setMinValue(minValue));
      dispatch(setSteps(intervals.length));
      itWasEditingLegends.current = true;
    }
  }, [dispatch, isEditLegendVisible, intervals]);

  const shouldReturnEditLegend = isEditLegendVisible && Boolean(intervals.length);

  if (!shouldReturnEditLegend) {
    return null;
  }

  return (
    <EditLegend
      decrementSteps={onDecrementSteps}
      incrementSteps={onIncrementSteps}
      intervals={intervals}
      isFormInvalid={isFormInvalid()}
      legend={legend}
      onCancel={onToggleEditLegend}
      onSave={handleSubmit}
      setMax={setMax}
      setMin={setMin}
    />
  );
};
